import { Matrix4 } from 'three';

/** The small part of InstancedMesh used for slot management; it can be tested without WebGL. */
export interface InstanceTarget {
  count: number;
  instanceMatrix: { needsUpdate: boolean };
  setMatrixAt(index: number, matrix: Matrix4): void;
  getMatrixAt(index: number, matrix: Matrix4): void;
}

/** Compact, reusable instance slots keyed by entity id (GDD §15.7, #70). */
export class InstancedBatch {
  private readonly indices = new Map<string, number>();
  private readonly ids: string[] = [];
  private readonly scratch = new Matrix4();

  constructor(readonly mesh: InstanceTarget, readonly capacity: number) {
    if (!Number.isInteger(capacity) || capacity < 0) throw new Error('Invalid instance capacity');
    mesh.count = 0;
  }

  indexOf(id: string): number | undefined {
    return this.indices.get(id);
  }

  /** Aligned with the live instance slots, including after compaction. */
  get names(): readonly string[] {
    return this.ids;
  }

  set(id: string, matrix: Matrix4): void {
    let index = this.indices.get(id);
    if (index === undefined) {
      if (this.ids.length === this.capacity) throw new Error('Instance capacity exceeded');
      index = this.ids.length;
      this.ids.push(id);
      this.indices.set(id, index);
      this.mesh.count = this.ids.length;
    }
    this.mesh.setMatrixAt(index, matrix);
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  release(id: string): void {
    const index = this.indices.get(id);
    if (index === undefined) throw new Error(`Unknown instance id: ${id}`);
    const lastIndex = this.ids.length - 1;
    const lastId = this.ids[lastIndex]!;
    if (index !== lastIndex) {
      this.mesh.getMatrixAt(lastIndex, this.scratch);
      this.mesh.setMatrixAt(index, this.scratch);
      this.ids[index] = lastId;
      this.indices.set(lastId, index);
    }
    this.ids.pop();
    this.indices.delete(id);
    this.mesh.count = this.ids.length;
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
